import * as THREE from 'three';
import {
  MELEE_RANGE,
  UNIT_RADIUS,
  abilityDef,
  auraDef,
  zoneDef,
  type AbilityDef,
  type AbilityId,
  type AuraId,
  type School,
  type ZoneId,
  type ZoneSnap,
} from '@arena/shared';
import { settings } from '../lib/settings.svelte';
import type { HeldItem, HeldModels, UnitView } from './UnitView';

export const SCHOOL_COLORS: Record<School, number> = {
  physical: 0xffffff,
  holy: 0xfff0a0,
  fire: 0xff7a2a,
  frost: 0x9fe3ff,
  arcane: 0xd78cff,
  shadow: 0x8a4ad6,
  nature: 0x6ad16a,
};

const MODELS = {
  arrow: 'forest/weapon-arrow',
  hammer: 'survival/tool-hammer-upgraded',
} satisfies Record<string, HeldItem>;
/** Held-item models the effects borrow; load them into `SpellFx.models`. */
export const FX_MODELS: HeldItem[] = Object.values(MODELS);

const RED = 0xd11f1f;
const CRIMSON = 0xe0203a;
const BLOOD = 0xa00d0d;
const GOLD = 0xffd45a;
const HOLY = SCHOOL_COLORS.holy;
const FROST = SCHOOL_COLORS.frost;
const FIRE = SCHOOL_COLORS.fire;
const ARCANE = SCHOOL_COLORS.arcane;
const SHADOW = SCHOOL_COLORS.shadow;
const NATURE = SCHOOL_COLORS.nature;
const LEAF = 0x9ef07a;
const BARK = 0x8a6a3a;
const MOON = 0xc8b8ff;
const SMOKE = 0x5d5d6a;
const DUST = 0xb59a78;
const WHITE = 0xffffff;
const ALLY = 0x5ad17a;
const LIGHTNING = 0xb8e0ff;
const WATER = 0x4fb4ff;
/** Each totem's color: its lantern's tint and the ring it's planted with. */
export const TOTEMS: Partial<Record<ZoneId, number>> = {
  'healing-stream-totem': WATER,
  'earthbind-totem': 0xa0703c,
  'tremor-totem': 0xe0b040,
  'grounding-totem': 0xe8f4ff,
  'war-banner': CRIMSON,
  'spirit-lantern': 0xfff3c4,
};
const CHEST = 0.9;
const HEAD = 1.4;
export const ARROW_SPEED = 80;
/** Scratch transform for each burst particle's instance matrix. */
const particle = new THREE.Object3D();

interface Effect {
  object: THREE.Object3D;
  start: number;
  end: number;
  update: (progress: number, age: number) => void;
}

interface Units {
  source: UnitView;
  /** The caster for self-cast abilities. */
  target: UnitView;
}
type Step = (fx: SpellFx, units: Units) => void;

interface Projectile {
  color: number;
  model?: keyof typeof MODELS;
  size?: number;
  tail?: boolean;
  /** Pellets flying in a spread. */
  count?: number;
  /** Gives an instant ability a purely visual flight at this speed. */
  speed?: number;
}

interface Recipe {
  /** Tints the cast glow and any area ring; defaults to the school color. */
  color?: number;
  cast?: Step;
  projectile?: Projectile;
  /** On the target once the ability lands. */
  impact?: Step;
  /** Lasts the whole channel. */
  channel?: (fx: SpellFx, units: Units, duration: number) => Effect;
  /** Each channel tick. */
  tick?: Step;
  /** Where its trap goes off. */
  sprung?: (fx: SpellFx, point: THREE.Vector3) => void;
}

interface BurstOptions {
  count?: number;
  speed?: number;
  size?: number;
  life?: number;
  gravity?: number;
  /** Extra upward speed. */
  up?: number;
  shape?: 'spark' | 'shard' | 'drop' | 'smoke';
  /** Biases particles this way; `spread` sets how far they scatter around it. */
  dir?: THREE.Vector3;
  spread?: number;
  flat?: boolean;
  /** Particles converge on the origin instead of flying out. */
  inward?: boolean;
  /** World height particles come to rest on. */
  floor?: number;
}

interface SlashOptions {
  color: number;
  /** 0 is a vertical stroke, π/2 horizontal. */
  tilt?: number;
  height?: number;
  size?: number;
  delay?: number;
}

const at = (view: UnitView, height: number) =>
  view.root.position.clone().setY(view.root.position.y + height);

const between = (a: UnitView, b: UnitView) =>
  a.root.position.distanceTo(b.root.position);

/** Horizontal unit vector from one unit to another. */
const direction = (from: UnitView, to: UnitView) => {
  const d = to.root.position.clone().sub(from.root.position).setY(0);
  return d.lengthSq() > 1e-6 ? d.normalize() : new THREE.Vector3(0, 0, -1);
};

const randomDir = () =>
  new THREE.Vector3(
    Math.random() * 2 - 1,
    Math.random() * 2 - 1,
    Math.random() * 2 - 1,
  ).normalize();

const basic = (
  color: number,
  opacity = 1,
  side: THREE.Side = THREE.FrontSide,
) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    side,
    // Spell light is meant to read at full strength, not be graded with the scene.
    toneMapped: false,
  });

/** Every material the effects make. */
export function fxShaders(): THREE.Object3D {
  return new THREE.Group().add(
    ...[basic(WHITE), basic(WHITE, 1, THREE.DoubleSide)].map(
      (material) => new THREE.Mesh(new THREE.BufferGeometry(), material),
    ),
    new THREE.InstancedMesh(new THREE.BufferGeometry(), basic(WHITE), 1),
  );
}

/** How far from its center an area ability reaches a unit's center. */
export function areaRadius(ability: AbilityDef): number {
  for (const effect of ability.effects) {
    if (effect.type === 'aoe') return effect.radius + UNIT_RADIUS;
    if (effect.type === 'zone')
      return zoneDef(effect.zone).radius + UNIT_RADIUS;
  }
  return UNIT_RADIUS;
}

/** A faint disc with a bright rim on the ground; the disc is the first child. */
export function areaDecal(radius: number, color: number): THREE.Group {
  const group = new THREE.Group();
  for (const [geometry, opacity] of [
    [new THREE.CircleGeometry(radius, 48), 0.15],
    [new THREE.RingGeometry(radius * 0.94, radius, 48), 0.7],
  ] as const) {
    const mesh = new THREE.Mesh(
      geometry,
      basic(color, opacity, THREE.DoubleSide),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.06;
    group.add(mesh);
  }
  return group;
}

/** A tapered arc whose midpoint sits at the origin, bowing toward -x. */
function crescent(
  radius: number,
  width: number,
  arc: number,
): THREE.BufferGeometry {
  const segments = 16;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = (t - 0.5) * arc;
    const w = width * Math.sin(Math.PI * t);
    for (const r of [radius - w, radius])
      positions.push(Math.cos(angle) * r - radius, Math.sin(angle) * r, 0);
    if (i < segments) {
      const k = i * 2;
      indices.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  return geometry;
}

/** Frees an effect's geometry and materials; meshes cloned from a loaded model share theirs. */
export function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh) || child.userData.shared) return;
    if (child instanceof THREE.InstancedMesh) child.dispose();
    child.geometry.dispose();
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    for (const material of materials) material.dispose();
  });
}

const slashes =
  (...strokes: SlashOptions[]): Step =>
  (fx, { source, target }) => {
    for (const stroke of strokes) fx.slash(target, source, stroke);
  };

const bleed =
  (amount: number): Step =>
  (fx, { source, target }) =>
    fx.blood(target, direction(source, target), amount);

const steps =
  (...list: Step[]): Step =>
  (fx, units) => {
    for (const step of list) step(fx, units);
  };

const sparks =
  (color: number, options: BurstOptions = {}, height = CHEST): Step =>
  (fx, { target }) =>
    fx.burst(at(target, height), color, { count: 12, speed: 3, ...options });

const flare =
  (color: number, size?: number): Step =>
  (fx, { target }) =>
    fx.flare(target, color, size);

const rise =
  (color: number, count?: number): Step =>
  (fx, { target }) =>
    fx.rise(target, color, count);

const pillar =
  (color: number, options?: Parameters<SpellFx['pillar']>[2]): Step =>
  (fx, { target }) =>
    fx.pillar(target, color, options);

const puff =
  (color: number, size?: number, height = CHEST): Step =>
  (fx, { target }) =>
    fx.puff(at(target, height), color, size);

const zap: Step = (fx, { source, target }) =>
  fx.bolt(source.handPosition(new THREE.Vector3()), at(target, CHEST));

const totem =
  (zone: ZoneId): Step =>
  (fx, { source }) => {
    fx.ring(source.root.position, 1.6, TOTEMS[zone] ?? NATURE, 0.4);
    fx.puff(at(source, 0.2), DUST, 0.7);
  };

const RECIPES: Partial<Record<AbilityId, Recipe>> = {
  'pvp-trinket': {
    impact: steps(
      flare(0xfff3c4),
      sparks(WHITE, { count: 16, speed: 4, shape: 'shard', life: 0.5 }),
    ),
  },
  bandage: {
    channel: (fx, { source, target }, duration) =>
      fx.beam(source, target, WHITE, duration, 0.025),
    tick: rise(WHITE, 6),
  },
  'healing-potion': { impact: steps(flare(RED), rise(RED, 12)) },
  'regeneration-potion': { impact: rise(ALLY, 14) },
  refreshment: { tick: rise(WATER, 6) },

  'mortal-strike': {
    impact: steps(
      slashes(
        { color: RED, tilt: 0.8 },
        { color: RED, tilt: -0.8, delay: 0.07 },
      ),
      bleed(18),
    ),
  },
  hamstring: {
    impact: steps(
      slashes({ color: WHITE, tilt: Math.PI / 2, height: 0.35, size: 0.7 }),
      bleed(6),
    ),
  },
  rend: {
    impact: steps(slashes({ color: 0xb01818, tilt: 0.5 }), bleed(10)),
  },
  execute: {
    impact: steps(
      slashes({ color: RED, tilt: 0.1, size: 1.4 }),
      bleed(28),
      (fx, { target }) => fx.ring(target.root.position, 1.5, RED),
    ),
  },
  charge: {
    cast: (fx, { source, target }) => {
      const dash = abilityDef('charge').effects.find(
        (e) => e.type === 'charge',
      );
      const time =
        dash?.type === 'charge'
          ? Math.max(0, between(source, target) - MELEE_RANGE) / dash.speed
          : 0;
      fx.trail(source, DUST, time + 0.1);
      fx.after(time, () => {
        fx.ring(target.root.position, 2, DUST, 0.35);
        fx.burst(at(target, CHEST), WHITE, { count: 10, speed: 3 });
      });
    },
  },
  pummel: { impact: sparks(WHITE, { count: 8, shape: 'shard' }) },
  'intimidating-shout': {
    color: 0xc8643c,
    cast: (fx, { source }) =>
      fx.burst(at(source, HEAD), 0xc8643c, {
        count: 16,
        speed: 6,
        size: 0.08,
        life: 0.5,
        flat: true,
      }),
  },
  'berserker-rage': {
    impact: (fx, { target }) => {
      fx.flare(target, RED);
      target.flash(RED, 0.8);
    },
  },
  'shield-wall': { impact: flare(0xa9b8c8, 1.1) },
  'piercing-howl': {
    color: 0xd8c8a0,
    cast: (fx, { source }) =>
      fx.burst(at(source, HEAD), 0xd8c8a0, {
        count: 16,
        speed: 7,
        size: 0.07,
        life: 0.5,
        flat: true,
      }),
  },
  'war-banner': { cast: totem('war-banner') },

  frostbolt: {
    projectile: { color: FROST, tail: true, size: 0.2 },
    impact: steps(
      sparks(FROST, { count: 14, shape: 'shard', speed: 3.5 }),
      puff(0xe8f8ff, 0.6),
    ),
  },
  fireball: {
    projectile: { color: FIRE, tail: true, size: 0.28 },
    impact: steps(
      sparks(FIRE, { count: 18, speed: 4 }),
      sparks(0xffd27a, { count: 8, speed: 2.5 }),
      puff(0x4a3a32, 0.7),
    ),
  },
  'fire-blast': {
    impact: steps(
      sparks(FIRE, { count: 16, speed: 2.5, up: 3 }),
      puff(0x4a3a32, 0.5),
    ),
  },
  polymorph: {
    impact: steps(
      sparks(0xff9ad8, { count: 18, speed: 2 }),
      puff(0xffc4e8, 0.7),
    ),
  },
  'frost-nova': {
    cast: (fx, { source }) =>
      fx.burst(at(source, 0.2), FROST, {
        count: 24,
        speed: 9,
        shape: 'shard',
        life: 0.5,
        flat: true,
      }),
  },
  counterspell: {
    cast: (fx, { source, target }) => fx.beam(source, target, ARCANE, 0.3),
    impact: sparks(ARCANE, { count: 10 }),
  },
  blink: {
    cast: (fx, { source }) => {
      fx.puff(at(source, CHEST), ARCANE, 0.8);
      fx.after(0.15, () => fx.puff(at(source, CHEST), ARCANE, 0.8));
    },
  },
  'ice-barrier': { impact: flare(FROST) },
  'ice-block': {
    impact: sparks(FROST, { count: 16, shape: 'shard', speed: 3 }),
  },
  'ice-lance': {
    projectile: { color: FROST, tail: true, size: 0.12 },
    impact: sparks(FROST, { count: 12, shape: 'shard', speed: 3 }),
  },
  'rune-of-detonation': {
    sprung: (fx, point) => {
      const blast = point.clone().setY(point.y + 0.3);
      fx.burst(blast, FIRE, { count: 30, speed: 7, up: 2, life: 0.6 });
      fx.burst(blast, 0xffd27a, { count: 12, speed: 4, up: 3 });
      fx.puff(blast, 0x4a3a32, 1.4);
    },
  },

  'flash-heal': { impact: pillar(HOLY, { radius: 0.55 }) },
  renew: { impact: rise(HOLY, 14) },
  'power-word-shield': { impact: flare(HOLY) },
  'pain-suppression': {
    impact: pillar(0xe8eef8, { fromAbove: true, radius: 0.45, life: 1 }),
  },
  'shadow-word-pain': {
    impact: steps(sparks(SHADOW), puff(SHADOW, 0.6)),
  },
  'mind-blast': {
    impact: (fx, { target }) => {
      fx.burst(at(target, HEAD), SHADOW, {
        count: 18,
        speed: 3,
        life: 0.35,
        inward: true,
      });
      fx.after(0.3, () => fx.puff(at(target, HEAD), SHADOW, 0.8));
    },
  },
  'mind-flay': {
    channel: (fx, { source, target }, duration) =>
      fx.beam(source, target, SHADOW, duration, 0.07),
    tick: sparks(SHADOW, { count: 8, speed: 2 }),
  },
  'psychic-scream': {
    color: SHADOW,
    cast: (fx, { source }) => fx.puff(at(source, HEAD), SHADOW, 1.2),
  },
  'dispel-magic': {
    cast: (fx, { source, target }) => fx.beam(source, target, WHITE, 0.25),
    impact: rise(WHITE, 16),
  },
  silence: {
    impact: steps(
      sparks(SHADOW, { count: 10, shape: 'shard' }, HEAD),
      puff(SHADOW, 0.5, HEAD),
    ),
  },
  'mass-dispel': { color: WHITE },
  'spirit-lantern': { cast: totem('spirit-lantern') },

  'sinister-strike': {
    impact: steps(
      slashes(
        { color: 0xfff6c8, tilt: 0.9 },
        { color: 0xfff6c8, tilt: -0.6, delay: 0.08 },
      ),
      bleed(6),
    ),
  },
  eviscerate: {
    impact: steps(
      slashes(
        { color: CRIMSON, tilt: 0.8 },
        { color: CRIMSON, tilt: -0.8, delay: 0.06 },
      ),
      bleed(20),
    ),
  },
  'kidney-shot': {
    impact: steps(
      sparks(WHITE, { count: 10, shape: 'shard' }, 0.7),
      (fx, { target }) => fx.ring(target.root.position, 1.2, WHITE, 0.3),
    ),
  },
  'cheap-shot': { impact: sparks(0xffe066, {}, HEAD) },
  gouge: {
    impact: slashes({
      color: 0xffe066,
      tilt: Math.PI / 2,
      height: HEAD,
      size: 0.6,
    }),
  },
  kick: { impact: sparks(WHITE, { count: 8, shape: 'shard' }) },
  stealth: { cast: (fx, { source }) => fx.vanish(source) },
  vanish: { cast: (fx, { source }) => fx.vanish(source) },
  sprint: {
    cast: (fx, { source }) => {
      fx.puff(at(source, 0.2), DUST, 0.8);
      fx.trail(source, DUST, 0.6);
    },
  },
  blind: { impact: puff(0xd8d0b0, 0.6, HEAD) },
  'cloak-of-shadows': {
    impact: steps(flare(0x3a2a4a, 1.2), puff(SMOKE, 0.8)),
  },
  caltrops: { color: 0xa9b8c8 },

  'steady-shot': {
    projectile: { color: WHITE, model: 'arrow' },
    impact: steps(sparks(WHITE, { count: 6 }), bleed(5)),
  },
  'arcane-shot': {
    projectile: { color: ARCANE, model: 'arrow', tail: true, size: 0.07 },
    impact: sparks(ARCANE, { count: 14 }),
  },
  'aimed-shot': {
    projectile: { color: 0xfff2c0, model: 'arrow', tail: true, size: 0.09 },
    impact: steps(sparks(WHITE, { count: 10, speed: 4 }), bleed(12)),
  },
  'serpent-sting': {
    projectile: {
      color: NATURE,
      model: 'arrow',
      tail: true,
      size: 0.05,
      speed: ARROW_SPEED,
    },
    impact: sparks(NATURE),
  },
  'concussive-shot': {
    projectile: { color: 0x8fb8ff, model: 'arrow', tail: true, size: 0.06 },
    impact: steps(sparks(0x8fb8ff), (fx, { target }) =>
      fx.ring(target.root.position, 1.2, 0x8fb8ff, 0.35),
    ),
  },
  'scatter-shot': {
    projectile: { color: 0xdddddd, count: 6, size: 0.06, speed: ARROW_SPEED },
    impact: sparks(WHITE, { speed: 3 }),
  },
  'silencing-shot': {
    projectile: {
      color: 0xb07cff,
      model: 'arrow',
      tail: true,
      size: 0.06,
      speed: ARROW_SPEED,
    },
    impact: sparks(0xb07cff, {}, HEAD),
  },
  'tranquilizing-shot': {
    projectile: {
      color: NATURE,
      model: 'arrow',
      tail: true,
      size: 0.06,
      speed: ARROW_SPEED,
    },
    impact: rise(NATURE, 14),
  },
  disengage: {
    cast: (fx, { source }) => {
      fx.puff(at(source, 0.2), DUST, 1);
      fx.trail(source, DUST, 0.35);
    },
  },
  'freezing-trap': {
    sprung: (fx, point) =>
      fx.burst(point.clone().setY(point.y + 0.3), FROST, {
        count: 24,
        speed: 3,
        up: 3,
        shape: 'shard',
        gravity: 8,
        life: 0.7,
      }),
  },
  flare: { color: 0xfff0b0 },

  'crusader-strike': {
    impact: steps(
      slashes({ color: GOLD, tilt: 0.7 }),
      sparks(GOLD, { count: 10 }),
    ),
  },
  judgement: {
    impact: (fx, { target }) => {
      fx.pillar(target, GOLD, { fromAbove: true, radius: 0.5, life: 0.6 });
      fx.after(0.12, () => fx.ring(target.root.position, 1.6, GOLD, 0.4));
    },
  },
  'hammer-of-wrath': {
    projectile: { color: GOLD, model: 'hammer', tail: true, size: 0.1 },
    impact: steps(sparks(GOLD, { count: 18, speed: 4 }), (fx, { target }) =>
      fx.ring(target.root.position, 1.5, GOLD),
    ),
  },
  'hammer-of-justice': {
    projectile: { color: GOLD, model: 'hammer', speed: 40 },
    impact: sparks(GOLD, {}, HEAD),
  },
  'flash-of-light': {
    impact: pillar(GOLD, { radius: 0.45, life: 0.7 }),
  },
  'holy-light': {
    impact: steps(pillar(GOLD, { radius: 0.8, life: 1.1 }), rise(GOLD, 20)),
  },
  cleanse: { impact: rise(WHITE, 16) },
  'blessing-of-freedom': { impact: flare(0xffb060) },
  'divine-shield': {
    impact: steps(flare(GOLD, 1.3), sparks(GOLD, { count: 16 })),
  },
  repentance: {
    impact: steps(
      sparks(GOLD, { count: 14, speed: 2 }, HEAD),
      puff(0xfff3c4, 0.6, HEAD),
    ),
  },
  'blessing-of-protection': { impact: flare(0xc8d8ff, 1.2) },
  'hallowed-ground': { color: GOLD },

  rejuvenation: { impact: rise(LEAF, 14) },
  regrowth: {
    impact: steps(pillar(LEAF, { radius: 0.55 }), rise(LEAF, 12)),
  },
  moonfire: {
    impact: pillar(MOON, { fromAbove: true, radius: 0.4, life: 0.7 }),
  },
  wrath: {
    projectile: { color: NATURE, tail: true, size: 0.18 },
    impact: steps(sparks(NATURE, { count: 14, speed: 3 }), puff(LEAF, 0.5)),
  },
  'entangling-roots': {
    impact: (fx, { target }) => {
      fx.burst(at(target, 0.1), BARK, {
        count: 14,
        speed: 1.5,
        up: 2.5,
        gravity: 6,
        shape: 'shard',
        life: 0.6,
      });
      fx.ring(target.root.position, 1.2, NATURE, 0.4);
    },
  },
  cyclone: {
    impact: steps(
      sparks(0xe6efe6, { count: 18, speed: 2.5, up: 3, flat: true }, 0.3),
      puff(0xe6efe6, 0.8),
    ),
  },
  barkskin: { impact: flare(BARK, 1.1) },
  'bear-form': { cast: (fx, { source }) => fx.shapeshift(source) },
  'travel-form': { cast: (fx, { source }) => fx.shapeshift(source) },
  'mighty-bash': {
    impact: steps(
      sparks(WHITE, { count: 12, shape: 'shard' }, HEAD),
      (fx, { target }) => fx.ring(target.root.position, 1.4, BARK, 0.35),
    ),
  },
  thornfield: { color: LEAF },

  'lightning-bolt': {
    color: LIGHTNING,
    cast: zap,
    impact: sparks(LIGHTNING, { count: 14, speed: 4, shape: 'shard' }),
  },
  'chain-lightning': {
    color: LIGHTNING,
    cast: zap,
    impact: sparks(LIGHTNING, { count: 12, speed: 4, shape: 'shard' }),
  },
  'flame-shock': {
    impact: steps(
      sparks(FIRE, { count: 14, speed: 1.5, up: 3 }),
      puff(0x4a3a32, 0.5),
    ),
  },
  'frost-shock': {
    impact: steps(
      sparks(FROST, { count: 14, shape: 'shard', speed: 3 }),
      (fx, { target }) => fx.ring(target.root.position, 1.2, FROST, 0.35),
    ),
  },
  'wind-shear': {
    cast: (fx, { source, target }) => fx.beam(source, target, 0xd8f0ff, 0.2),
    impact: sparks(0xd8f0ff, { count: 10, shape: 'shard' }),
  },
  purge: {
    cast: (fx, { source, target }) => fx.beam(source, target, LIGHTNING, 0.25),
    impact: rise(LIGHTNING, 16),
  },
  hex: {
    impact: steps(sparks(0x7ad17a, { count: 18, speed: 2 }), puff(0x9ee09e)),
  },
  'healing-wave': {
    color: WATER,
    impact: steps(pillar(WATER, { radius: 0.7, life: 1 }), rise(WATER, 16)),
  },
  'chain-heal': { color: WATER, impact: rise(WATER, 14) },
  riptide: {
    color: WATER,
    impact: steps(pillar(WATER, { radius: 0.45, life: 0.6 }), rise(WATER)),
  },
  'healing-stream-totem': { cast: totem('healing-stream-totem') },
  'earthbind-totem': { cast: totem('earthbind-totem') },
  'tremor-totem': { cast: totem('tremor-totem') },
  'grounding-totem': { cast: totem('grounding-totem') },
};

/** Abilities without a recipe still flash something in their school's color. */
const fallback = (ability: AbilityDef): Recipe => ({
  impact: (fx, { source, target }) => {
    const color = SCHOOL_COLORS[ability.school];
    if (target === source) fx.flare(target, color);
    else fx.burst(at(target, CHEST), color, { count: 12, speed: 3 });
  },
});

/** Spell visuals: casts, projectiles, impacts, ticks. Driven by combat events, on its own clock. */
export class SpellFx {
  models: HeldModels = {};
  private time = 0;
  private effects: Effect[] = [];
  private timers: { at: number; run: () => void }[] = [];
  private casts = new Map<string, Effect[]>();
  private zoneDecals = new Map<number, THREE.Group>();

  constructor(
    private scene: THREE.Scene,
    private view: (id: string) => UnitView | undefined,
  ) {}

  update(dt: number): void {
    this.time += dt;
    const due = this.timers.filter((t) => t.at <= this.time);
    this.timers = this.timers.filter((t) => t.at > this.time);
    for (const timer of due) timer.run();
    // Effects may spawn more while updating; those land in the fresh list.
    const current = this.effects;
    this.effects = [];
    const kept = current.filter((fx) => {
      const age = this.time - fx.start;
      const progress = Math.min(1, age / Math.max(1e-3, fx.end - fx.start));
      fx.update(progress, age);
      if (progress < 1) return true;
      this.scene.remove(fx.object);
      disposeObject(fx.object);
      return false;
    });
    this.effects = kept.concat(this.effects);
  }

  dispose(): void {
    for (const fx of this.effects) {
      this.scene.remove(fx.object);
      disposeObject(fx.object);
    }
    this.effects = [];
    this.timers = [];
    this.casts.clear();
    this.zones([], 0);
  }

  /** A decal on every zone in the snapshot, green for our team's and red for theirs, filled once armed. */
  zones(zones: ZoneSnap[], team: 0 | 1): void {
    const live = new Set(zones.map((z) => z.id));
    for (const [id, decal] of this.zoneDecals) {
      if (live.has(id)) continue;
      this.scene.remove(decal);
      disposeObject(decal);
      this.zoneDecals.delete(id);
    }
    for (const zone of zones) {
      let decal = this.zoneDecals.get(zone.id);
      if (!decal) {
        decal = areaDecal(
          zoneDef(zone.zoneId).radius + UNIT_RADIUS,
          zone.team === team ? ALLY : RED,
        );
        decal.position.set(...zone.pos);
        this.scene.add(decal);
        this.zoneDecals.set(zone.id, decal);
      }
      decal.children[0].visible = zone.armed;
    }
  }

  after(seconds: number, run: () => void): void {
    if (seconds <= 0) run();
    else this.timers.push({ at: this.time + seconds, run });
  }

  // -------------------------------------------------------------------------------------------
  // Combat events
  // -------------------------------------------------------------------------------------------

  castStart(
    sourceId: string,
    targetId: string | null,
    abilityId: AbilityId,
    duration: number,
    point?: [number, number, number],
  ): void {
    this.endCast(sourceId);
    const units = this.units(sourceId, targetId);
    if (!units) return;
    const ability = abilityDef(abilityId);
    const recipe = RECIPES[abilityId];
    const color = recipe?.color ?? SCHOOL_COLORS[ability.school];
    const effects = [this.glow(units.source, color, duration)];
    if (recipe?.channel && units.target !== units.source)
      effects.push(recipe.channel(this, units, duration));
    if (point && ability.channel)
      effects.push(this.storm(point, areaRadius(ability), color, duration));
    this.casts.set(sourceId, effects);
  }

  castStop(sourceId: string): void {
    this.endCast(sourceId);
  }

  castSuccess(
    sourceId: string,
    targetId: string | null,
    abilityId: AbilityId,
    travel: number,
    point?: [number, number, number],
  ): void {
    this.endCast(sourceId);
    const units = this.units(sourceId, targetId);
    if (!units) return;
    const { source, target } = units;
    const ability = abilityDef(abilityId);
    const recipe = RECIPES[abilityId] ?? fallback(ability);
    const color = recipe.color ?? SCHOOL_COLORS[ability.school];
    const center = point ? new THREE.Vector3(...point) : source.root.position;
    for (const effect of ability.effects)
      if (effect.type === 'aoe')
        this.ring(center, effect.radius + UNIT_RADIUS, color);
    recipe.cast?.(this, units);

    let flight = travel;
    const shot =
      recipe.projectile ?? (travel > 0 ? { color, tail: true } : undefined);
    if (shot && target !== source) {
      if (!flight && shot.speed) flight = between(source, target) / shot.speed;
      if (flight > 0) this.projectile(source, target, flight, shot);
    }
    const damages = ability.effects.some((e) => e.type === 'damage');
    this.after(flight, () => {
      recipe.impact?.(this, units);
      if (damages) target.flash(WHITE, 0.15);
    });
  }

  /** A chain leaping from one unit to the next, landing like the first hit. */
  chain(sourceId: string, targetId: string, abilityId: AbilityId): void {
    const source = this.view(sourceId);
    const target = this.view(targetId);
    if (!source || !target) return;
    const recipe = RECIPES[abilityId];
    const color = recipe?.color ?? SCHOOL_COLORS[abilityDef(abilityId).school];
    this.bolt(at(source, CHEST), at(target, CHEST), color);
    recipe?.impact?.(this, { source, target });
  }

  /** A trap going off: a ring over any area it hits, then its recipe. */
  sprung(abilityId: AbilityId, point: [number, number, number]): void {
    const center = new THREE.Vector3(...point);
    const recipe = RECIPES[abilityId];
    const color = recipe?.color ?? SCHOOL_COLORS[abilityDef(abilityId).school];
    for (const effect of abilityDef(abilityId).effects) {
      if (effect.type !== 'zone') continue;
      for (const hit of zoneDef(effect.zone).effects)
        if (hit.type === 'aoe')
          this.ring(center, hit.radius + UNIT_RADIUS, color);
    }
    recipe?.sprung?.(this, center);
  }

  channelTick(abilityId: AbilityId, sourceId: string, targetId: string): void {
    const recipe = RECIPES[abilityId];
    const units = this.units(sourceId, targetId);
    if (recipe?.tick && units && abilityDef(abilityId).channel)
      recipe.tick(this, units);
  }

  melee(sourceId: string, targetId: string, ranged: boolean): void {
    const units = this.units(sourceId, targetId);
    if (!units) return;
    const { source, target } = units;
    if (ranged) {
      const flight = between(source, target) / ARROW_SPEED;
      this.projectile(source, target, flight, { color: WHITE, model: 'arrow' });
      this.after(flight, () => {
        this.burst(at(target, CHEST), WHITE, { count: 5, speed: 2 });
        target.flash(WHITE, 0.1);
      });
      return;
    }
    const side = Math.random() < 0.5 ? -1 : 1;
    this.slash(target, source, {
      color: WHITE,
      tilt: side * (0.9 + Math.random() * 0.4),
      size: 0.75,
    });
    this.burst(at(target, CHEST), 0xfff2c0, {
      count: 5,
      speed: 2.5,
      size: 0.04,
      life: 0.3,
    });
    target.flash(WHITE, 0.1);
  }

  /** A damage-over-time tick in `school`, or a heal-over-time tick for null. */
  periodic(targetId: string, school: School | null): void {
    const target = this.view(targetId);
    if (!target) return;
    if (school === null) return this.rise(target, HOLY, 8);
    target.flash(WHITE, 0.1);
    const chest = at(target, CHEST);
    switch (school) {
      case 'physical':
        return this.blood(target, randomDir().setY(0).normalize(), 6);
      case 'fire':
        return this.burst(chest, FIRE, { count: 10, speed: 1, up: 2.5 });
      case 'shadow':
        return this.puff(chest, SHADOW, 0.6);
      case 'nature':
        return this.burst(chest, NATURE, {
          count: 10,
          speed: 0.8,
          up: 1.5,
          size: 0.07,
          life: 0.7,
          shape: 'drop',
        });
      default:
        return this.burst(chest, SCHOOL_COLORS[school], { count: 8 });
    }
  }

  /** The target's cast shatters into shards of the school it lost. */
  interrupted(targetId: string, school: School): void {
    const target = this.view(targetId);
    if (!target) return;
    this.burst(
      target.handPosition(new THREE.Vector3()),
      SCHOOL_COLORS[school],
      {
        count: 16,
        speed: 3.5,
        shape: 'shard',
        gravity: 6,
        life: 0.6,
      },
    );
  }

  auraRemoved(
    targetId: string,
    auraId: AuraId,
    reason: 'expired' | 'dispelled' | 'broken' | 'cancelled',
  ): void {
    const target = this.view(targetId);
    if (!target) return;
    if (auraId === 'stealth') return this.vanish(target);
    if (auraDef(auraId).form) return this.shapeshift(target);
    if (auraId === 'ice-block' || auraId === 'freezing-trap')
      return this.burst(at(target, CHEST), FROST, {
        count: 20,
        speed: 3,
        shape: 'shard',
        gravity: 8,
        life: 0.7,
      });
    if (reason !== 'dispelled' && reason !== 'broken') return;
    if (auraId === 'polymorph')
      return this.puff(at(target, CHEST), 0xffc4e8, 0.9);
    this.burst(at(target, CHEST), WHITE, {
      count: 10,
      speed: 2,
      up: 1.5,
      shape: 'shard',
    });
  }

  /** A smoke puff where a unit slips into stealth. */
  vanish(view: UnitView): void {
    this.puff(at(view, CHEST), SMOKE, 1.3);
  }

  /** A burst of leaves where a druid changes shape. */
  shapeshift(view: UnitView): void {
    this.puff(at(view, CHEST), LEAF, 1.1);
  }

  // -------------------------------------------------------------------------------------------
  // Primitives
  // -------------------------------------------------------------------------------------------

  burst(origin: THREE.Vector3, color: number, options: BurstOptions): void {
    const {
      count = 10,
      speed = 3,
      size = 0.06,
      life = 0.5,
      gravity = 0,
      up = 0,
      shape = 'spark',
      dir,
      spread = 1,
      flat = false,
      inward = false,
      floor,
    } = options;
    const geometry =
      shape === 'shard'
        ? new THREE.TetrahedronGeometry(size)
        : shape === 'spark'
          ? new THREE.OctahedronGeometry(size)
          : new THREE.IcosahedronGeometry(size, 0);
    const opacity = shape === 'smoke' ? 0.5 : 1;
    const material = basic(color, opacity);
    const parts = Array.from(
      {
        length: Math.max(
          1,
          Math.round(count * (settings.effects === 'low' ? 0.4 : 1)),
        ),
      },
      () => {
        const v = randomDir().multiplyScalar(spread);
        if (dir) v.add(dir);
        if (flat) v.y = 0;
        v.normalize().multiplyScalar(speed * (0.5 + Math.random() * 0.5));
        v.y += up;
        return {
          v,
          tumble: [Math.random() * 6, Math.random() * 6],
          spin: (Math.random() - 0.5) * 12,
        };
      },
    );
    // One draw call for the whole burst.
    const mesh = new THREE.InstancedMesh(geometry, material, parts.length);
    mesh.position.copy(origin);
    mesh.frustumCulled = false;
    const rest = floor === undefined ? -Infinity : floor - origin.y + size;
    this.spawn(mesh, life, (p, age) => {
      parts.forEach(({ v, tumble, spin }, i) => {
        if (inward) {
          particle.position.copy(v).multiplyScalar((1 - p) * life);
        } else {
          particle.position.copy(v).multiplyScalar(age);
          particle.position.y = Math.max(
            rest,
            particle.position.y - 0.5 * gravity * age * age,
          );
        }
        particle.rotation.set(tumble[0], tumble[1], spin * age);
        particle.scale.setScalar(shape === 'smoke' ? 1 + p * 1.5 : 1 - p * 0.7);
        particle.updateMatrix();
        mesh.setMatrixAt(i, particle.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      material.opacity = opacity * (1 - p * p);
    });
  }

  puff(origin: THREE.Vector3, color: number, size = 1): void {
    this.burst(origin, color, {
      count: Math.round(10 * size),
      speed: 1.4 * size,
      up: 0.5,
      size: 0.22 * size,
      life: 0.9,
      shape: 'smoke',
    });
  }

  rise(view: UnitView, color: number, count = 10): void {
    this.burst(at(view, 0.3), color, {
      count,
      speed: 0.6,
      up: 2,
      size: 0.05,
      life: 0.9,
    });
  }

  /** Droplets thrown away from the hit that land and leave a fading splat. */
  blood(target: UnitView, dir: THREE.Vector3, amount: number): void {
    const feet = target.root.position.clone();
    this.burst(at(target, CHEST), BLOOD, {
      count: amount,
      speed: 3.5,
      size: 0.05,
      life: 0.7,
      gravity: 12,
      up: 2,
      dir,
      spread: 0.8,
      shape: 'drop',
      floor: feet.y,
    });
    this.after(0.25, () => this.splat(feet, dir, Math.ceil(amount / 6)));
  }

  private splat(feet: THREE.Vector3, dir: THREE.Vector3, count: number): void {
    const geometry = new THREE.CircleGeometry(1, 10).rotateX(-Math.PI / 2);
    const material = basic(0x6e0a0a, 0.85);
    material.polygonOffset = true;
    material.polygonOffsetFactor = -1;
    const group = new THREE.Group();
    group.position.copy(feet).setY(feet.y + 0.02);
    for (let i = 0; i < count; i++) {
      const mesh = new THREE.Mesh(geometry, material);
      const r = 0.1 + Math.random() * 0.18;
      mesh.scale.set(r, 1, r * (0.6 + Math.random() * 0.6));
      mesh.rotation.y = Math.random() * Math.PI;
      mesh.position
        .copy(dir)
        .multiplyScalar(0.3 + Math.random() * 0.9)
        .add(
          new THREE.Vector3(
            Math.random() - 0.5,
            0,
            Math.random() - 0.5,
          ).multiplyScalar(0.5),
        );
      group.add(mesh);
    }
    this.spawn(group, 5, (p) => {
      material.opacity = 0.85 * Math.min(1, (1 - p) * 3);
    });
  }

  /** A crescent swept across the target, facing the attacker. */
  slash(target: UnitView, source: UnitView, options: SlashOptions): void {
    const {
      color,
      tilt = 0.5,
      height = CHEST,
      size = 0.9,
      delay = 0,
    } = options;
    if (delay > 0)
      return this.after(delay, () =>
        this.slash(target, source, { ...options, delay: 0 }),
      );
    const material = basic(color, 0.95, THREE.DoubleSide);
    const mesh = new THREE.Mesh(
      crescent(size * 1.8, size * 0.22, 1.1),
      material,
    );
    const group = new THREE.Group();
    const toward = direction(target, source);
    group.position.copy(at(target, height)).addScaledVector(toward, 0.45);
    group.lookAt(group.position.clone().add(toward));
    group.add(mesh);
    this.spawn(group, 0.32, (p) => {
      const sweep = 1 - (1 - Math.min(1, p / 0.45)) ** 3;
      mesh.rotation.z = tilt + 0.6 * (1 - sweep);
      mesh.scale.setScalar(0.4 + 0.6 * sweep);
      material.opacity = 0.95 * (1 - Math.max(0, p - 0.45) / 0.55);
    });
  }

  /** An expanding ring on the ground. */
  ring(feet: THREE.Vector3, radius: number, color: number, life = 0.45): void {
    const material = basic(color, 0.7, THREE.DoubleSide);
    const mesh = new THREE.Mesh(new THREE.RingGeometry(0.85, 1, 48), material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.copy(feet).setY(feet.y + 0.1);
    this.spawn(mesh, life, (p) => {
      mesh.scale.setScalar(Math.max(0.01, p * radius));
      material.opacity = 0.7 * (1 - p);
    });
  }

  /** A column of light on a unit; `fromAbove` slams it down first. */
  pillar(
    target: UnitView,
    color: number,
    options: { radius?: number; life?: number; fromAbove?: boolean } = {},
  ): void {
    const { radius = 0.65, life = 0.9, fromAbove = false } = options;
    const height = 3.2;
    const material = basic(color, 0.55, THREE.DoubleSide);
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius, height, 20, 1, true).translate(
        0,
        height / 2,
        0,
      ),
      material,
    );
    const group = new THREE.Group();
    group.add(mesh);
    this.spawn(group, life, (p) => {
      group.position.copy(target.root.position);
      if (fromAbove) mesh.position.y = Math.max(0, 1 - p / 0.2) * height;
      const s = 1 - p * 0.75;
      mesh.scale.set(s, 1, s);
      material.opacity = 0.55 * (1 - p);
    });
    this.burst(at(target, 0.2), color, {
      count: 12,
      speed: 0.6,
      up: 2.2,
      size: 0.05,
      life,
    });
  }

  /** A ring that rises up a unit, trailing motes. */
  flare(view: UnitView, color: number, size = 0.9): void {
    const material = basic(color, 0.8, THREE.DoubleSide);
    const mesh = new THREE.Mesh(
      new THREE.RingGeometry(0.75, 1, 32).rotateX(-Math.PI / 2),
      material,
    );
    this.spawn(mesh, 0.7, (p) => {
      mesh.position
        .copy(view.root.position)
        .setY(view.root.position.y + 0.1 + p * 1.8);
      mesh.scale.setScalar(size * (1 - p * 0.5));
      material.opacity = 0.8 * (1 - p);
    });
    this.burst(at(view, 0.3), color, {
      count: 14,
      speed: 0.8,
      up: 2.5,
      life: 0.8,
    });
  }

  /** A line from the source's hand to the target. */
  beam(
    source: UnitView,
    target: UnitView,
    color: number,
    life: number,
    width = 0.05,
  ): Effect {
    const material = basic(color, 0.8);
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(width, width, 1, 6, 1, true)
        .rotateX(Math.PI / 2)
        .translate(0, 0, 0.5),
      material,
    );
    const from = new THREE.Vector3();
    return this.spawn(mesh, life, (p, age) => {
      source.handPosition(from);
      const to = at(target, CHEST);
      const length = from.distanceTo(to);
      mesh.visible = length > 0.3;
      mesh.position.copy(from);
      mesh.lookAt(to);
      mesh.scale.set(1, 1, length);
      const pulse = 0.55 + 0.3 * Math.sin(age * 25);
      material.opacity = life < 0.5 ? 0.85 * (1 - p) : pulse;
    });
  }

  /** A jagged flash of lightning from one point to another. */
  bolt(from: THREE.Vector3, to: THREE.Vector3, color = LIGHTNING): void {
    const material = basic(color, 0.95);
    const geometry = new THREE.CylinderGeometry(0.035, 0.035, 1, 4, 1, true)
      .rotateX(Math.PI / 2)
      .translate(0, 0, 0.5);
    const group = new THREE.Group();
    const segments = 8;
    const jitter = from.distanceTo(to) * 0.04;
    let last = from.clone();
    for (let i = 1; i <= segments; i++) {
      const next = from.clone().lerp(to, i / segments);
      if (i < segments) next.add(randomDir().multiplyScalar(jitter));
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(last);
      mesh.lookAt(next);
      mesh.scale.set(1, 1, last.distanceTo(next));
      group.add(mesh);
      last = next;
    }
    this.spawn(group, 0.25, (p) => {
      material.opacity = 0.95 * (1 - p) * (0.6 + 0.4 * Math.random());
    });
  }

  /** Dust puffs left behind a moving unit. */
  trail(view: UnitView, color: number, life: number): void {
    let next = 0;
    this.spawn(new THREE.Group(), life, (_, age) => {
      for (; next <= age; next += 0.05)
        this.burst(at(view, 0.15), color, {
          count: 2,
          speed: 0.4,
          up: 0.6,
          size: 0.12,
          life: 0.5,
          shape: 'smoke',
        });
    });
  }

  projectile(
    source: UnitView,
    target: UnitView,
    flight: number,
    shot: Projectile,
  ): void {
    const { color, model, size = 0.2, tail = false, count = 1 } = shot;
    const group = new THREE.Group();
    const template = model && this.models[MODELS[model]];
    const spinners: THREE.Object3D[] = [];
    const holders = Array.from({ length: count }, () => {
      const holder = new THREE.Group();
      if (template) {
        const body = template.clone();
        body.traverse((child) => (child.userData.shared = true));
        if (model === 'arrow') {
          body.scale.setScalar(2);
          holder.add(body);
        } else {
          body.scale.setScalar(3);
          body.position.y = -0.28;
          const spinner = new THREE.Group();
          spinner.add(body);
          spinners.push(spinner);
          holder.add(spinner);
        }
      } else {
        holder.add(
          new THREE.Mesh(new THREE.IcosahedronGeometry(size, 1), basic(color)),
          new THREE.Mesh(
            new THREE.IcosahedronGeometry(size * 1.6, 1),
            basic(color, 0.3),
          ),
        );
      }
      // Models are about 0.9 long, so their tail starts behind them.
      const behind = template ? 0.4 : 0;
      if (tail)
        holder.add(
          new THREE.Mesh(
            new THREE.ConeGeometry(size, size * 8, 8, 1, true)
              .rotateX(-Math.PI / 2)
              .translate(0, 0, -size * 4 - behind),
            basic(color, 0.5),
          ),
        );
      group.add(holder);
      return holder;
    });
    const offsets = holders.map(() =>
      count === 1 ? new THREE.Vector3() : randomDir().multiplyScalar(0.8),
    );
    const from = source.handPosition(new THREE.Vector3());
    this.spawn(group, flight, (p, age) => {
      const to = at(target, CHEST);
      holders.forEach((holder, i) => {
        holder.position
          .copy(from)
          .lerp(to, p)
          .addScaledVector(offsets[i], Math.sin(Math.PI * p));
        holder.lookAt(to);
      });
      for (const spinner of spinners) spinner.rotation.x = age * 18;
    });
  }

  /** A channeled ground area, its color falling on it in shards. */
  private storm(
    point: [number, number, number],
    radius: number,
    color: number,
    life: number,
  ): Effect {
    const decal = areaDecal(radius, color);
    decal.position.set(...point);
    let next = 0;
    return this.spawn(decal, life, (_, age) => {
      if (age < next) return;
      next = age + 0.08;
      const angle = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * radius;
      const from = new THREE.Vector3(
        Math.cos(angle) * r,
        4,
        Math.sin(angle) * r,
      );
      this.burst(from.add(decal.position), color, {
        count: 2,
        speed: 0.5,
        gravity: 30,
        shape: 'shard',
        size: 0.1,
        life: 0.6,
        floor: decal.position.y,
      });
    });
  }

  /** An orb at the caster's hand while a cast or channel runs. */
  private glow(source: UnitView, color: number, life: number): Effect {
    const group = new THREE.Group();
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.08, 1),
      basic(color),
    );
    const shell = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.16, 1),
      basic(color, 0.35),
    );
    group.add(core, shell);
    return this.spawn(group, life, (p, age) => {
      source.handPosition(group.position);
      shell.scale.setScalar((0.6 + 0.6 * p) * (1 + 0.15 * Math.sin(age * 14)));
    });
  }

  private spawn(
    object: THREE.Object3D,
    life: number,
    update: Effect['update'],
  ): Effect {
    this.scene.add(object);
    const effect = { object, start: this.time, end: this.time + life, update };
    update(0, 0);
    this.effects.push(effect);
    return effect;
  }

  private endCast(sourceId: string): void {
    for (const effect of this.casts.get(sourceId) ?? [])
      effect.end = Math.min(effect.end, this.time);
    this.casts.delete(sourceId);
  }

  private units(sourceId: string, targetId: string | null): Units | null {
    const source = this.view(sourceId);
    if (!source) return null;
    return { source, target: (targetId && this.view(targetId)) || source };
  }
}
